/******************************************************************************
 *
 * Copyright (c) 2019-2022 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Shivam Mishra' <shivam.mishra@wakencode.com>, June 2022
 *
 *****************************************************************************/

import React, { useEffect, useState } from "react";
import Styles from "./WorkSizeDetails.module.css";
import { prepareCompanyDetails } from "../../container/postDataFormat";
import { updateInfo } from "../../network";
import { string } from "../../constant/string";
import Loader from "../../loader/WhiteLoader";
import { useAuth } from "../../context/AuthContext";
import { useUser } from "../../context/UserContext";

/**
 * Work details information page called in Wrapper.js
 * This page takes work details information and update in
 * local storage and api
 *
 * @param {String} setPage State for current page
 * @param {String} org State for user type
 * @param {Boolean} isEmployee State for employee type
 * @param {Boolean} isInvited State for user invitaion
 * @returns JSX for the work details information page
 */
const WorkSizeDetails = ({ setPage, org, isInvited, isEmployee, jobRole }) => {
  // State for company name and skills
  const [companyInfo, setCompanyInfo] = useState({
    subscriberName: "",
    zipCode: "",
  });
  // State for error
  const [error, setError] = useState({
    subscriberName: "",
    zipCode: "",
  });
  // State for loader
  const [loading, setLoading] = useState(false);
  //State to set user data from user context
  const { userData, setUserData } = useUser("");
  // State to set token from auth context
  const { token, setToken } = useAuth("");

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  /**
   *  Pre-populate data if present in userContext
   */
  useEffect(() => {
    setCompanyInfo((prev) => ({
      ...prev,
      subscriberName: userData?.subscriberDetails?.name,
      zipCode: userData?.subscriberDetails?.address?.zipCode,
    }));
  }, []);

  /**
   * Check if user information are valid or not
   *
   * @returns {Boolean}
   */
  const checkValidation = () => {
    let flag = true;
    if (!companyInfo.subscriberName.trim()) {
      flag = false;
      // Set error for subscriber name
      setError((prevState) => {
        return {
          ...prevState,
          subscriberName: string.REQUIRED,
        };
      });
    }
    if (!companyInfo.zipCode && !isInvited) {
      flag = false;
      // Set error for zipcode
      setError((prevState) => {
        return {
          ...prevState,
          zipCode: string.REQUIRED,
        };
      });
    }
    if (companyInfo.zipCode && companyInfo.zipCode.length < 5) {
      flag = false;
      // Set error for zipcode
      setError((prevState) => {
        return {
          ...prevState,
          zipCode: string.INVALID_ZIP_CODE,
        };
      });
    }
    return flag;
  };

  /**
   * Next button handler
   * @param {*} e Event for Next button click
   */
  const handleNextButtonChange = (e) => {
    e.preventDefault();
    // Check if user information is valid or not
    let valid = checkValidation();

    if (valid) {
      setUserData((prev) => ({
        ...prev,
        subscriberDetails: {
          ...prev?.subscriberDetails,
          name: companyInfo.subscriberName,
          address: {
            ...prev?.subscriberDetails?.address,
            zipCode: companyInfo.zipCode,
          },
        },
      }));
      callApiToSaveData();
    }
  };

  /**
   * Call update api to save all the user entered information
   */
  const callApiToSaveData = () => {
    setLoading(true);

    // Prepare correct format for request body
    let postData = prepareCompanyDetails(userData, companyInfo);

    // call api to save/update data
    updateInfo(postData, config)
      .then(() => {
        // Update current page
        if (jobRole === "employee") {
          setPage("OwnerDetails");
        } else {
          setPage("ActivatingAccount");
        }
        setLoading(false);
      })
      .catch((err) => {
        if (err?.response?.status === 500) {
          setError(string.SOMETHING_WENT_WRONG_TRY_AGAIN);
        } else if(err?.response?.status === 401) {
          setToken("")
          setPage("SignUp");
        } else if (err.response && err.response.data && err.response.data.errorDescription) {
          setError(err.response.data.errorDescription);
        }
        setLoading(false);
      });
  };

  /**
   * Back button handler
   *
   * @param {*} e Event for Back button click
   */
  const handleBackButtonChange = (e) => {
    e.preventDefault();
    // Update current page
    if (isInvited) {
      setPage("PersonalDetails");
    } else {
      if (jobRole === "employee") {
        setPage("PersonalDetails");
      } else {
        setPage("WorkDetails");
      }
    }
  };

  /**
   * Handle input change
   *
   * @param {MouseEvent} e Input change event
   */
  const handleCompanyInfoBlur = (e) => {
    const { name, value } = e.target;
    setCompanyInfo((prev) => {
      return {
        ...prev,
        [name]: value.trim(),
      };
    });
  };

  const handleCompanyInfoChange = (e) => {
    const { name, value } = e.target;

    // Clear error state for selected field
    setError((prevState) => {
      return {
        ...prevState,
        [name]: "",
      };
    });

    // Set state for selected field
    if (name === "zipCode") {
      let code =  value.replace(/\D/g, "").slice(0, 5);
      
      if (code === "" || /^[0-9]+$/.test(code)) {
        setCompanyInfo((prev) => {
          return {
            ...prev,
            zipCode: code.slice(0, 5),
          };
        });
      }
    } else {
      setCompanyInfo((prev) => {
        return {
          ...prev,
          [name]: value.trimStart().replace(/ {2,}/g, " "),
        };
      });
    }
  };

  return (
    <div className="main">
      {/* Head text */}
      <div className="headText">{string.WORK_DETAILS}</div>
      {/* Line under head text */}
      <div className="line"></div>
      {/* Sub-heading under line */}
      <div className="text">{isInvited ? string.YOUR_COMPANY_DETAILS : string.ENTER_YOUR_COMPANY_DETAILS_BELOW}</div>
      {/* Company name input field */}
      <div className="tenREM">
        <input
          className={isInvited ? "disabled-input" : "input"}
          placeholder={string.COMPANY_NAME}
          name="subscriberName"
          onChange={handleCompanyInfoChange}
          onBlur={handleCompanyInfoBlur}
          value={companyInfo.subscriberName}
          readOnly={isInvited}
        ></input>
        {error.subscriberName ? <div className="errorText">{error.subscriberName}</div> : null}
      </div>
      {/* Zip code input field */}
      <div className="tenREM">
        <input
          className={isInvited ? "disabled-input" : "input"}
          placeholder={string.ZIP_CODE}
          name="zipCode"
          value={companyInfo.zipCode}
          onChange={handleCompanyInfoChange}
          readOnly={isInvited}
        ></input>
        {error.zipCode ? <div className="errorText">{error.zipCode}</div> : null}
      </div>
      {/* Next button */}
      <div>
        <button className="nextButton" onClick={!loading ? handleNextButtonChange : null}>
          {loading ? <Loader /> : string.NEXT}
        </button>
      </div>
      {/* Back button */}
      <div>
        <button className="backButton" onClick={handleBackButtonChange}>
          {string.BACK}
        </button>
      </div>
    </div>
  );
};

export default WorkSizeDetails;
